import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import MessagingSettings from "../components/MessagingSettings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, User, Lock, Mail, Shield, Save, Eye, EyeOff, Moon, Sun, Globe, CreditCard, ShieldCheck, Trash2, AlertTriangle } from "lucide-react";
import { API } from "../App";
import { LANGUAGES } from "../lib/languages";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";

const ProfilePage = ({ user, logout, checkAuth }) => {
  const { t, locale, setLocale } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [name, setName] = useState(user?.name || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetting2fa, setResetting2fa] = useState(false);
  const [deletionRequest, setDeletionRequest] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");
  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [deletionLoaded, setDeletionLoaded] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#messaging") {
      const timer = setTimeout(() => document.getElementById("messaging")?.scrollIntoView({ behavior: "smooth" }), 300);
      return () => clearTimeout(timer);
    }
  }, [hash]);

  // Load deletion request status for patients
  useState(() => {
    if (user?.role === "patient") {
      axios.get(`${API}/patient/deletion-request`).then(res => {
        setDeletionRequest(res.data);
        setDeletionLoaded(true);
      }).catch(() => setDeletionLoaded(true));
    }
  }, [user]);

  const handleReset2fa = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser votre 2FA ? Vous devrez le reconfigurer à la prochaine connexion.")) return;
    setResetting2fa(true);
    try {
      await axios.post(`${API}/auth/2fa/reset`);
      toast.success("2FA réinitialisé. Veuillez vous reconnecter.");
      if (logout) logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setResetting2fa(false);
    }
  };

  const handleDeletionRequest = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir demander la suppression de vos données ? Cette action est irréversible.")) return;
    setRequestingDeletion(true);
    try {
      const res = await axios.post(`${API}/patient/deletion-request`, { reason: deletionReason });
      setDeletionRequest(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setRequestingDeletion(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/profile`, { name: name.trim() });
      toast.success(t("profile.profileUpdated"));
      if (checkAuth) checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword.length < 6) {
      toast.error(t("auth.passwordMinLength"));
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    setSavingPassword(true);
    try {
      await axios.post(`${API}/auth/set-password`, { new_password: passwordData.newPassword });
      toast.success(user?.has_password ? t("profile.passwordChanged") : t("profile.passwordSet"));
      setPasswordData({ newPassword: "", confirmPassword: "" });
      if (checkAuth) checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6 max-w-3xl" data-testid="profile-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to={user?.role === "therapist" ? "/therapist" : "/patient"}>
            <Button variant="ghost" size="icon" data-testid="profile-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("profile.title")}</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("profile.subtitle")}</p>
          </div>
        </div>

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {t("profile.accountInfo")}
            </CardTitle>
            <CardDescription>{t("profile.accountDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("auth.email")}</p>
                <p className="font-medium" data-testid="profile-email">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <User className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{t("profile.role")}</p>
                <p className="font-medium capitalize" data-testid="profile-role">
                  {user?.role === "therapist" ? t("auth.therapist") : t("auth.patient")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              {t("profile.editProfile")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name">{t("auth.fullName")}</Label>
                <Input
                  id="profile-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="profile-name-input"
                />
              </div>
              <Button type="submit" disabled={savingProfile} data-testid="profile-save-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? t("common.loading") : t("common.save")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Appearance & Language */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              {theme === "dark" ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-primary" />}
              {t("profile.appearance")} & {t("profile.language")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === "dark" ? <Moon className="w-4 h-4 text-muted-foreground" /> : <Sun className="w-4 h-4 text-muted-foreground" />}
                <span>{theme === "dark" ? t("profile.darkMode") : t("profile.lightMode")}</span>
              </div>
              <Button variant="outline" size="sm" onClick={toggleTheme} data-testid="profile-theme-toggle">
                {theme === "dark" ? t("profile.lightMode") : t("profile.darkMode")}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span>{t("profile.language")}</span>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {LANGUAGES.map((l) => (
                  <Button key={l.id} variant={locale === l.id ? "default" : "outline"} size="sm" onClick={() => setLocale(l.id)} data-testid={`lang-${l.id}-btn`}>
                    {l.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              {locale === "fr" ? "Abonnement" : "Subscription"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Link to="/pricing">
              <Button variant="outline" className="w-full gap-2" data-testid="go-pricing-btn">
                <CreditCard className="w-4 h-4" />
                {t("profile.manageSubscription")}
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Set/Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              {user?.has_password ? t("profile.changePassword") : t("profile.setPassword")}
            </CardTitle>
            <CardDescription>
              {user?.has_password ? t("profile.changePasswordDesc") : t("profile.setPasswordDesc")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!user?.has_password && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-200" data-testid="google-password-notice">
                {t("profile.googleNotice")}
              </div>
            )}
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t("profile.newPassword")}</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    placeholder="Min. 6"
                    data-testid="set-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">{t("profile.confirmPassword")}</Label>
                <Input
                  id="confirm-new-password"
                  type={showPassword ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  data-testid="confirm-set-password-input"
                />
              </div>
              <Button type="submit" disabled={savingPassword} data-testid="set-password-btn">
                <Lock className="w-4 h-4 mr-2" />
                {savingPassword ? t("common.loading") : (user?.has_password ? t("common.edit") : t("profile.setPassword"))}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* 2FA Management — Therapists only */}
        {user?.role === "therapist" && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Double authentification (2FA)
              </CardTitle>
              <CardDescription>
                La double authentification protège votre compte avec un code temporaire en plus de votre mot de passe.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <ShieldCheck className="w-4 h-4 text-green-500" />
                <div>
                  <p className="text-sm font-medium">
                    {user?.totp_enabled ? "2FA activé" : "2FA non configuré"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.totp_enabled
                      ? "Votre compte est protégé par la double authentification"
                      : "Le 2FA sera configuré lors de votre prochaine connexion"}
                  </p>
                </div>
              </div>
              {user?.totp_enabled && (
                <Button
                  variant="outline"
                  className="w-full text-destructive hover:text-destructive"
                  onClick={handleReset2fa}
                  disabled={resetting2fa}
                  data-testid="reset-2fa-btn"
                >
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {resetting2fa ? "Réinitialisation..." : "Réinitialiser le 2FA"}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Messagerie & IA — Therapists only */}
        {user?.role === "therapist" && <MessagingSettings />}

        {/* Data Deletion — Patients only */}
        {user?.role === "patient" && (
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Suppression des données
              </CardTitle>
              <CardDescription>
                Conformément au RGPD, vous pouvez demander la suppression de vos données. En vertu des obligations HAS, les données de santé doivent être conservées 20 ans après le dernier contact.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {deletionRequest?.status === "pending" ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg" data-testid="deletion-pending-notice">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Demande en cours</p>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        Votre demande de suppression a été envoyée le {new Date(deletionRequest.requested_at).toLocaleDateString("fr-FR")}.
                        {!deletionRequest.is_eligible && (
                          <> Vos données sont conservables jusqu'au {new Date(deletionRequest.eligible_date).toLocaleDateString("fr-FR")} (obligation HAS).</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ) : deletionRequest?.status === "rejected" ? (
                <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg" data-testid="deletion-rejected-notice">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Demande refusée</p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                    Motif : {deletionRequest.rejection_reason}
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="deletion-reason">Motif de la demande (optionnel)</Label>
                    <Input
                      id="deletion-reason"
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      placeholder="Indiquez la raison de votre demande..."
                      data-testid="deletion-reason-input"
                    />
                  </div>
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleDeletionRequest}
                    disabled={requestingDeletion}
                    data-testid="request-deletion-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {requestingDeletion ? "Envoi..." : "Demander la suppression de mes données"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
